import { useState, useEffect, useRef } from 'react';

interface UseElementSizeProps {
  el?: HTMLElement | undefined | null;
}

/**
 * Hook that returns the size of an element and updates when it changes
 * @returns [ref, size] where ref is the reference to the element and size is the current size
 */
export function useElementSize<T extends HTMLElement = HTMLDivElement>({
  el: elFromProps,
}: UseElementSizeProps) {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = elFromProps ?? ref.current;
    if (!element) return;

    setSize({
      width: element.offsetWidth,
      height: element.offsetHeight,
    });

    const resizeObserver = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return;

      const entry = entries[0];
      const { width, height } = entry.contentRect;

      setSize((localSize) => {
        if (width !== localSize.width || height !== localSize.height) {
          return { width, height };
        }
        return localSize;
      });
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [elFromProps]);

  return {
    size,
    ref,
  } as const;
}
